import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../../lib/firebase';
import { Link } from 'react-router-dom';
import AppBar from '../../../components/AppBar';
import { GlobalInput } from '../../../components/GlobalInput';
import { GlobalButton } from '../../../components/GlobalButton';
import styles from './page.module.css';
import { Toast } from '../../../components/Toast';
import ContentGrid from '../../../components/ContentGrid';
import AppFooter from '../../../components/AppFooter';
import { useCompanies } from '../../../hooks/useCompanies';
import { useLocale } from '../../../context/DynamicLocaleProvider';
import { getAuthErrorMessage } from '../../../utils/authErrors';
import { SignupStep } from './types';

export default function EmployeeSignupPage() {
  const { t } = useLocale();
  const { employeeRegistrationCode } = useParams<{ employeeRegistrationCode: string }>();
  const navigate = useNavigate();
  const { findCompanyByEmployeeCode } = useCompanies(false);
  
  const [step, setStep] = useState<SignupStep>('code');
  const [company, setCompany] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  
  // Form states
  const [employeeCode, setEmployeeCode] = useState(employeeRegistrationCode || '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verifiedUser, setVerifiedUser] = useState<any>(null);

  // Load company if employeeRegistrationCode is provided in URL
  useEffect(() => {
    if (employeeRegistrationCode) {
      loadCompany(employeeRegistrationCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeRegistrationCode]);

  const loadCompany = async (code: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const companyData = await findCompanyByEmployeeCode(code);
      if (companyData) {
        setCompany(companyData);
        setStep('verify');
      } else {
        setError(t('employee_signup_invalid_code'));
        setShowToast(true);
      }
    } catch (err) {
      setError(t('employee_signup_failed_load_company'));
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!employeeCode.trim()) {
      setError(t('employee_signup_enter_code'));
      setShowToast(true);
      return;
    }
    
    await loadCompany(employeeCode.trim());
  };

  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const requestData = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        companyId: company.id,
        employeeRegistrationCode: company.employeeRegistrationCode,
        role: "client_admin"
      };


      // Call the Cloud Function to verify the employee
      const response = await fetch('https://verifyemployee-kahfqpgsrq-ey.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });


      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || t('employee_signup_verify_failed'));
        setShowToast(true);
        return;
      }

      const data = await response.json();

      if (!data.success) {
        setError(data.error || t('employee_signup_no_employee_found'));
        setShowToast(true);
        return;
      }

      // Store the verified user data and proceed to signup
      setVerifiedUser({
        id: data.userId,
        firstName: data.employeeDetails.firstName,
        lastName: data.employeeDetails.lastName,
      });
      setStep('signup');
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Network error:', err);
      }
      setError(t('employee_signup_verify_failed'));
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (password !== confirmPassword) {
      setError(t('employee_signup_passwords_do_not_match'));
      setLoading(false);
      setShowToast(true);
      return;
    }

    if (!verifiedUser?.id) {
      setError(t('employee_signup_verification_required'));
      setLoading(false);
      setShowToast(true);
      return;
    }

    try {
      // Create Firebase auth account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update Firebase user profile with name
      await updateProfile(user, {
        displayName: `${firstName} ${lastName}`
      });

      // Call Cloud Function to finalize signup (migrate document, delete old, send notifications)
      const finalizeUserSignup = httpsCallable<{ oldUserId: string }, { success: boolean; message: string; newUid: string; oldUserId: string }>(functions, 'finalizeUserSignup');
      
      const result = await finalizeUserSignup({
        oldUserId: verifiedUser.id
      });

      if (result.data.success) {

        setShowToast(true);
        // Redirect to home after successful signup
        setTimeout(() => {
          navigate('/home');
        }, 2000);
      } else {
        throw new Error('Failed to finalize user signup');
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Signup error:', err);
      }
      const message = getAuthErrorMessage(err, t, 'employee_signup');
      setError(message ?? t('employee_signup_unexpected_error'));
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 'code':
        return (
          <>
            {/* Headline & paragraph */}
            <h1 className={styles.title}>{t('employee_signup_title')}</h1>
            <p className={styles.subtitle}>{t('employee_signup_subtitle')}</p>

            {/* Form */}
            <form onSubmit={handleCodeSubmit} className={styles.form}>
              <GlobalInput
                label={t('employee_signup_code_label')}
                type="text"
                value={employeeCode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmployeeCode(e.target.value)}
                required
                error={error || undefined}
              />

              {/* Button */}
              <div className={styles.buttonContainer}>
                <GlobalButton
                  type="submit"
                  isLoading={loading}
                  disabled={loading}
                >
                  {t('employee_signup_continue_button')}
                </GlobalButton>
              </div>
            </form>
          </>
        );

      case 'verify':
        return (
          <>
            {/* Headline & paragraph */}
            <h1 className={styles.title}>{t('employee_signup_verification_title')}</h1>
            {company && (
              <div className={styles.companyName}>{company.name}</div>
            )}
            <p className={styles.subtitle}>{t('employee_signup_verification_subtitle')}</p>

            {/* Form */}
            <form onSubmit={handleVerifySubmit} className={styles.form}>
              <GlobalInput
                label={t('employee_signup_first_name_label')}
                type="text"
                value={firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                required
                error={error || undefined}
              />

              <GlobalInput
                label={t('employee_signup_last_name_label')}
                type="text"
                value={lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                required
              />

              {/* Button */}
              <div className={styles.buttonContainer}>
                <GlobalButton
                  type="submit"
                  isLoading={loading}
                  disabled={loading}
                >
                  {t('employee_signup_verify_button')}
                </GlobalButton>
              </div>
            </form>
          </>
        );

      case 'signup':
        return (
          <>
            {/* Headline & paragraph */}
            <h1 className={styles.title}>{t('employee_signup_finalize_title')}</h1>
            {company && (
              <div className={styles.companyName}>{t('employee_signup_company_label')}: {company.name}</div>
            )}
            <p className={styles.subtitle}>{t('employee_signup_finalize_subtitle')}</p>

            {/* Form */}
            <form onSubmit={handleSignupSubmit} className={styles.form}>
              <GlobalInput
                label={t('employee_signup_email_label')}
                type="email"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                required
                error={error || undefined}
              />

              <GlobalInput
                label={t('employee_signup_password_label')}
                type="password"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                required
              />

              <GlobalInput
                label={t('employee_signup_confirm_password_label')}
                type="password"
                value={confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                required
              />

              {/* Button */}
              <div className={styles.buttonContainer}>
                <GlobalButton
                  type="submit"
                  isLoading={loading}
                  disabled={loading}
                >
                  {t('employee_signup_complete_button')}
                </GlobalButton>

                <div className={styles.securityBadge}>
                  <img src="/assets/icons/shield.lefthalf.filled.green.svg" alt="Shield" height={12} width={12} />
                  <span>{t('employee_signup_encrypted_badge')}</span>
                </div>
              </div>

              {/* Login alternative */}
              <p className={styles.signupText}>
                {t('employee_signup_already_have_account')}{' '}
                <Link to="/login" className={styles.link}>
                  {t('employee_signup_login_link')}
                </Link>
              </p>
            </form>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {/* App bar */}
      <AppBar />

      {/* Content */}
      <ContentGrid rows="1fr 1fr 1fr 48px">
        {/* Spacer */}
        <div style={{ gridRow: 1 }} />

        {/* Form container */}
        <div className={styles.formContainer}>
          {renderStep()}

          <Toast
            message={error ? error : t('employee_signup_success_message')}
            type={error ? 'error' : 'success'}
            isVisible={showToast}
            onClose={() => setShowToast(false)}
          />
        </div>

        {/* Spacer */}
        <div style={{ gridRow: 3 }} />

        {/* Footer */}
        <AppFooter inline />
      </ContentGrid>
    </div>
  );
}
